/**
 * Commitment state and copy, resolved from two server-written fields on the
 * subscription document: the `commitment` mirror and the `commitmentPending`
 * marker. Mirror present = committed; absent + marker = finalizing; neither =
 * old-model membership.
 *
 * The marker exists because the mirror's absence alone is ambiguous — it means
 * both "still provisioning" and "never had a term".
 *
 * No runtime dependencies, so mobile can import this too.
 */

import type { DunningState, PaymentMode } from "@lemonadetraining/contracts";
import { formatCommitmentDate, formatEuros } from "./commitmentPlans";
import { ACCESS_QUERY_STATUSES } from "./entitlementAccess";
import { MIN_COMMITMENT_MONTHS } from "./membershipLabels";

export { formatCommitmentDate, MIN_COMMITMENT_MONTHS };

/**
 * Structural rather than the canonical `SubscriptionCommitmentMirror`, so a raw
 * document read is assignable whatever Timestamp shape the SDK produced.
 */
export interface CommitmentMirrorLike {
  commitmentMonths?: unknown;
  commitmentEndDate?: unknown;
  paymentMode?: unknown;
  endBehavior?: unknown;
  dunningState?: unknown;
  scheduleReady?: unknown;
  paymentsLeft?: unknown;
  rollMonthlyAmount?: unknown;
  graceEndsAt?: unknown;
  pausedAt?: unknown;
  pauseState?: unknown;
  openRequestId?: unknown;
  cancelAtCommitmentEnd?: unknown;
  renewalWindowActive?: unknown;
}

/** A bound commitment. Every server figure is carried through untouched. */
export interface CommittedStatus {
  kind: "committed";
  commitmentMonths: number;
  /** Extends day-for-day on a dunning pause. */
  commitmentEndDate: Date;
  paymentMode: PaymentMode;
  endBehavior: string | null;
  /** Anything outside the published union reads as `none`. */
  dunningState: DunningState;
  /** Set while in grace; access is unaffected until then. */
  graceEndsAt: Date | null;
  /** Set while paused; the day-for-day extension is measured from it. */
  pausedAt: Date | null;
  paymentsLeft: number | null;
  /** Euros, not cents — the post-term roll price, never the current charge. */
  rollMonthlyAmount: number | null;
  monthsElapsed: number;
  /**
   * The member has asked to stop at the end date and can still take it back.
   * Stripe's own `cancel_at_period_end` stays false throughout a
   * schedule-managed term, so it is not a substitute for this field.
   */
  cancelAtCommitmentEnd: boolean;
  /** A still-pending ask sitting with the gym, or null. */
  openRequestId: string | null;
  /**
   * The renewal sweep has told this member their term is ending and the term
   * is still live. It is the ONLY thing that opens the renewal decision: no
   * client-side date arithmetic, so the card and the notice cannot disagree.
   */
  renewalWindowActive: boolean;
}

export type CommitmentStatus = CommittedStatus | { kind: "finalizing" } | { kind: "none" };

/**
 * Accepts a Timestamp (either SDK), a raw `{seconds}` object, a Date, epoch ms,
 * or an ISO string. Unreadable input returns null, never an Invalid Date.
 */
export function toDate(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value === "object") {
    const candidate = value as { toDate?: () => Date; seconds?: unknown; _seconds?: unknown };
    if (typeof candidate.toDate === "function") {
      const converted = candidate.toDate();
      return converted instanceof Date && !Number.isNaN(converted.getTime()) ? converted : null;
    }
    // Null-checked before coercion for the same reason as `finiteOrNull`:
    // `Number(null)` is 0, which would date a commitment to 1 Jan 1970.
    const raw = candidate.seconds ?? candidate._seconds;
    if (raw == null) return null;
    const seconds = Number(raw);
    if (Number.isFinite(seconds)) return new Date(seconds * 1000);
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? new Date(value) : null;
  }
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

/**
 * Keeps a real `0` (a paid-up term) but rejects an absent value — and null is
 * checked BEFORE coercion, because `Number(null)` is `0`, not `NaN`. Coercing
 * first would turn a missing `paymentsLeft` into "0 payments left" and a full
 * progress bar, which reads as a finished term rather than as missing data.
 */
function finiteOrNull(value: unknown): number | null {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Identical to the server's `wholeMonthsBetween`, end-of-month clamp included:
 * a term bought on the 31st has a whole month at Feb 28, because that is the
 * day Stripe bills it. Counting differently would make the progress bar
 * disagree with the "payments left" beside it.
 */
export function wholeMonthsBetween(from: Date | null, to: Date | null): number {
  if (!from || !to || to.getTime() <= from.getTime()) return 0;

  let months =
    (to.getUTCFullYear() - from.getUTCFullYear()) * 12 + (to.getUTCMonth() - from.getUTCMonth());

  if (to.getUTCDate() < from.getUTCDate()) {
    const lastDayOfToMonth = new Date(
      Date.UTC(to.getUTCFullYear(), to.getUTCMonth() + 1, 0)
    ).getUTCDate();
    if (to.getUTCDate() !== lastDayOfToMonth) months -= 1;
  }
  return months > 0 ? months : 0;
}

/**
 * The one computed figure here — no server field publishes it. Monthly reads
 * the server's own counter so the bar cannot disagree with "N payments left";
 * upfront has no payments to count, so it uses elapsed time.
 *
 * Never derived from `commitmentEndDate`: a dunning pause extends that date
 * day-for-day, which would run the bar BACKWARDS. Floor is 1 — day one is the
 * first month, not a zeroth one.
 */
export function resolveMonthsElapsed(input: {
  commitmentMonths: number;
  paymentMode: PaymentMode;
  paymentsLeft: number | null;
  startedAt: Date | null;
  now?: Date;
}): number {
  const { commitmentMonths, paymentMode, paymentsLeft, startedAt, now = new Date() } = input;

  const elapsed =
    paymentMode === "monthly" && paymentsLeft !== null
      ? commitmentMonths - paymentsLeft
      : wholeMonthsBetween(startedAt, now) + 1;

  if (!Number.isFinite(elapsed) || elapsed < 1) return 1;
  return Math.min(Math.round(elapsed), commitmentMonths);
}

function toDunningState(value: unknown): DunningState {
  return value === "grace" || value === "paused" ? value : "none";
}

/**
 * Stripe statuses under which the contract is still live. `past_due` is a
 * live membership in grace, not an ended one. Paused is status-blind: a
 * written-off episode flips the status back to `active`.
 */
export const LIVE_SUBSCRIPTION_STATUSES: readonly string[] = ACCESS_QUERY_STATUSES;

export function isLiveSubscriptionStatus(status: unknown): boolean {
  return typeof status === "string" && LIVE_SUBSCRIPTION_STATUSES.includes(status);
}

/**
 * Stripe statuses nothing resumes from. Deliberately NOT the complement of
 * LIVE_SUBSCRIPTION_STATUSES: `trialing`, `incomplete`, `paused` and `unpaid`
 * are neither live nor ended, and treating them as ended would take away a
 * member's own cancel entry — the § 312k failure this list exists to avoid.
 * Mobile keeps an identical copy.
 */
export const TERMINAL_SUBSCRIPTION_STATUSES: readonly string[] = ["canceled", "incomplete_expired"];

export function isTerminalSubscriptionStatus(status: unknown): boolean {
  return typeof status === "string" && TERMINAL_SUBSCRIPTION_STATUSES.includes(status);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Where the end date will land once the pause closes: whole days rounded UP,
 * the same member-favourable rule the server applies. A projection only; the
 * server writes the real figure when the interval closes.
 */
export function projectedPausedEndDate(
  endDate: Date,
  pausedAt: Date | null,
  now: Date = new Date()
): Date {
  if (!pausedAt) return endDate;
  const elapsedMs = now.getTime() - pausedAt.getTime();
  if (elapsedMs <= 0) return endDate;
  return new Date(endDate.getTime() + Math.ceil(elapsedMs / DAY_MS) * DAY_MS);
}

/** `commitmentPending` is a SIBLING of the mirror on the document, not inside it. */
export function resolveCommitmentStatus(input: {
  mirror?: CommitmentMirrorLike | null;
  commitmentPending?: unknown;
  status?: unknown;
  startedAt?: unknown;
  now?: Date;
}): CommitmentStatus {
  const { mirror, commitmentPending, status, startedAt, now } = input;

  // Strictly `true`: the server writes or deletes the field, and treating some
  // other value as pending would strand an old-model membership in a
  // finalizing state that nothing ever clears. The marker is also stale once
  // the subscription is terminal — the server re-stamps it from the ledger row
  // alone, and "we're setting up your membership" beside a Canceled badge is a
  // contradiction the athlete cannot resolve.
  const isPending = commitmentPending === true && !isTerminalSubscriptionStatus(status);
  const notCommitted: CommitmentStatus = isPending ? { kind: "finalizing" } : { kind: "none" };

  if (!mirror) return notCommitted;

  // The mirror is only ever written complete, so a partial one means the shape
  // changed upstream — and it also proves a commitment-shaped record exists,
  // since nothing writes this object onto an old-model membership. Fail closed
  // to finalizing (matches mobile): never a term out of half a record, and
  // never an "original plan" claim over a record that is commitment-shaped.
  const commitmentMonths = finiteOrNull(mirror.commitmentMonths);
  const commitmentEndDate = toDate(mirror.commitmentEndDate);
  const paymentMode = mirror.paymentMode === "upfront" ? "upfront" : "monthly";

  if (
    commitmentMonths === null ||
    !Number.isInteger(commitmentMonths) ||
    commitmentMonths < MIN_COMMITMENT_MONTHS ||
    !commitmentEndDate ||
    mirror.scheduleReady !== true
  ) {
    return { kind: "finalizing" };
  }

  const paymentsLeft = finiteOrNull(mirror.paymentsLeft);

  return {
    kind: "committed",
    commitmentMonths,
    commitmentEndDate,
    paymentMode,
    endBehavior: typeof mirror.endBehavior === "string" ? mirror.endBehavior : null,
    dunningState: toDunningState(mirror.dunningState),
    graceEndsAt: toDate(mirror.graceEndsAt),
    pausedAt: toDate(mirror.pausedAt),
    paymentsLeft,
    rollMonthlyAmount: finiteOrNull(mirror.rollMonthlyAmount),
    // Strictly `true` and a non-empty string: the server writes or deletes both
    // fields, so anything else is drift and must not read as an intent.
    cancelAtCommitmentEnd: mirror.cancelAtCommitmentEnd === true,
    renewalWindowActive: mirror.renewalWindowActive === true,
    openRequestId:
      typeof mirror.openRequestId === "string" && mirror.openRequestId.length > 0
        ? mirror.openRequestId
        : null,
    monthsElapsed: resolveMonthsElapsed({
      commitmentMonths,
      paymentMode,
      paymentsLeft,
      startedAt: toDate(startedAt),
      now,
    }),
  };
}

/**
 * What the member is being asked to decide as their term runs out, or nothing.
 * `roll` and `expire` are the two configured end behaviours; every other state
 * resolves to `none` rather than to a guess.
 */
export type RenewalNotice =
  | { kind: "none" }
  | { kind: "roll"; endDate: Date; rollMonthlyAmount: number | null }
  | { kind: "expire"; endDate: Date };

const NO_RENEWAL_NOTICE: RenewalNotice = { kind: "none" };

/**
 * Mirrored clause for clause with mobile so the two apps cannot show a member
 * different things. Grace deliberately still decides: the contract is live and
 * the end date is not moving.
 */
export function resolveRenewalNotice(
  status: CommitmentStatus,
  subscriptionStatus: unknown,
  cancelAtCommitmentEnd: boolean,
  now: Date = new Date()
): RenewalNotice {
  if (status.kind !== "committed") return NO_RENEWAL_NOTICE;
  if (status.renewalWindowActive !== true) return NO_RENEWAL_NOTICE;
  if (!isLiveSubscriptionStatus(subscriptionStatus)) return NO_RENEWAL_NOTICE;
  if (cancelAtCommitmentEnd) return NO_RENEWAL_NOTICE;
  // Same reason as the clause above: the member has already asked to stop, and
  // this one is still sitting with the gym. Offering "Cancel at end date" here
  // succeeds — no client or server precondition refuses it — which would leave
  // them holding a scheduled end date AND an open early-exit request.
  if (status.openRequestId !== null) return NO_RENEWAL_NOTICE;
  if (status.dunningState === "paused") return NO_RENEWAL_NOTICE;
  if (status.commitmentEndDate.getTime() <= now.getTime()) return NO_RENEWAL_NOTICE;
  if (status.endBehavior === "expire") {
    return { kind: "expire", endDate: status.commitmentEndDate };
  }
  if (status.endBehavior === "roll_monthly") {
    return {
      kind: "roll",
      endDate: status.commitmentEndDate,
      rollMonthlyAmount: status.rollMonthlyAmount,
    };
  }
  return NO_RENEWAL_NOTICE;
}

/** Claims no dates. */
export const FINALIZING_HEADER = "Finalizing your plan";

/**
 * Promises no automatic resolution on purpose: the pending marker also stands
 * indefinitely on a refused or misconfigured purchase, so "as soon as it's
 * ready" would be a standing false promise to someone whose money has moved.
 * Shared verbatim with mobile.
 */
export const FINALIZING_DETAIL =
  "We're setting up your membership. This usually only takes a moment. If it doesn't complete, your gym can help.";

export const GRANDFATHERED_NOTE = "This is your original membership plan.";

export const PAID_UPFRONT_LABEL = "Paid upfront";

/** A near-term charge needs no year. */
export function formatShortDate(date: Date, locale?: string): string {
  return date.toLocaleDateString(locale, { day: "numeric", month: "short" });
}

/** Uses the ICU-safe contract-date formatter — see formatCommitmentDate. */
export function commitmentHeaderLabel(date: Date, timeZone?: string | null): string {
  return `Committed until ${formatCommitmentDate(date, timeZone)}`;
}

/**
 * A membership that has ended. The date is dropped, never substituted: a term
 * cancelled early never reached its commitment end date or its period end, so
 * either stand-in would date the ending to a day nothing happened.
 */
export function endedLabel(date: Date | null, timeZone?: string | null): string {
  return date ? `Ended ${formatCommitmentDate(date, timeZone)}` : "Ended";
}

/** Always rendered as real text, so nothing depends on seeing the bar. */
export function commitmentProgressText(monthsElapsed: number, commitmentMonths: number): string {
  return `${monthsElapsed} of ${commitmentMonths} months`;
}

/** The bar's single announcement. */
export function commitmentProgressAriaLabel(
  monthsElapsed: number,
  commitmentMonths: number
): string {
  return `${commitmentProgressText(monthsElapsed, commitmentMonths)} elapsed`;
}

export function paymentsLeftLabel(paymentsLeft: number): string {
  return `${paymentsLeft} payment${paymentsLeft === 1 ? "" : "s"} left`;
}

export function nextChargeLabel(date: Date, locale?: string): string {
  return `Next charge ${formatShortDate(date, locale)}`;
}

const CURRENCY_SYMBOLS: Record<string, string> = { usd: "$", gbp: "£" };

/**
 * `unitAmount` is MINOR units, as the Stripe price carries it. Euros go
 * through the package's single euro formatter; the dashboard ships no locale
 * framework, so there is no comma-decimal form to honour here (mobile differs).
 */
export function formatMonthlyPrice(unitAmount: number, currency?: string | null): string {
  const code = (currency || "eur").toLowerCase();
  const amount = unitAmount / 100;
  if (code === "eur") return `${formatEuros(amount)}/mo`;
  const symbol = CURRENCY_SYMBOLS[code];
  return symbol
    ? `${symbol}${amount.toFixed(2)}/mo`
    : `${amount.toFixed(2)} ${code.toUpperCase()}/mo`;
}
